import React, { useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import {
  fmtFull,
  NOK,
  CurrencyConfig,
} from '../../../Components/Layout/SqlServer/Common/util';
import {
  ResponsiveContainer,
  Bar,
  BarChart,
  Legend,
  Tooltip,
  XAxis,
  YAxis,
} from '../../../lib/RechartsWrapper';
import { SCOPE_COLORS, TREND_SCOPES } from '../Model/Constants';
import { TrendChartRecord, useTrendData } from '../hooks/Usetrenddata';
import { TrendRow } from '../Model/CostType';

interface TrendTabProps {
  dailyData: TrendChartRecord[];
  monthlyData: TrendRow[];
  currency?: CurrencyConfig;
  baseCurrency?: string;
}

const TrendTab: React.FC<TrendTabProps> = ({
  dailyData,
  monthlyData,
  currency = NOK,
  baseCurrency = 'NOK',
}) => {
  const [granularity, setGranularity] = useState<'daily' | 'monthly'>('daily');

  const pivotedMonthly = useTrendData(monthlyData);
  const chartData = granularity === 'daily' ? dailyData : pivotedMonthly;

  const tooltipFmt = (v: unknown): [string] => [
    fmtFull(Number(v), currency, baseCurrency),
  ];

  const convertValue = (v: number): number => {
    if (baseCurrency === 'USD') return v * currency.rate;
    if (currency.code === 'NOK') return v;
    return v / 10.5;
  };

  return (
    <Paper
      sx={{
        p: 2.5,
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'text.secondary',
          }}
        >
          {granularity === 'daily' ? 'Daily' : 'Monthly'} Cost Trend by Scope
        </Typography>

        <ToggleButtonGroup
          value={granularity}
          exclusive
          onChange={(event, val) => val && setGranularity(val)}
          size="small"
          sx={{
            height: 28,
            '& .MuiToggleButton-root': {
              px: 2,
              fontSize: 10,
              fontWeight: 700,
              border: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { bgcolor: 'primary.dark' },
              },
            },
          }}
        >
          <ToggleButton value="daily">Daily</ToggleButton>
          <ToggleButton value="monthly">Monthly</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <ResponsiveContainer width="100%" height={340}>
        <BarChart
          data={chartData}
          barCategoryGap={granularity === 'daily' ? '20%' : '40%'}
        >
          <XAxis
            dataKey="date"
            tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
            tickFormatter={(d: string) =>
              granularity === 'daily' ? d.slice(5) : d
            }
          />
          <YAxis
            tick={{ fill: '#94a3b8', fontSize: 10, fontWeight: 600 }}
            tickFormatter={(v: number) => {
              const converted = convertValue(v);
              return converted >= 1000
                ? `${(converted / 1000).toFixed(0)}k`
                : `${converted.toFixed(0)}`;
            }}
          />
          <Tooltip
            contentStyle={{
              background: 'rgba(15, 23, 42, 0.9)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 12,
              fontSize: 11,
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
            }}
            formatter={tooltipFmt}
          />
          <Legend
            wrapperStyle={{
              pt: 20,
              fontSize: 11,
              fontWeight: 600,
              color: '#94a3b8',
            }}
            iconType="circle"
          />
          {TREND_SCOPES.map((scope) => (
            <Bar
              key={scope}
              dataKey={scope}
              stackId="a"
              fill={SCOPE_COLORS[scope]}
              radius={[2, 2, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default TrendTab;
